/**
 * @file        src/lib/listingQuery.ts
 * @description Pure, entity-agnostic listing engine: turns URL search params into a
 *              listing query, does the pagination math, and builds canonical listing
 *              URLs. No React, no fs, no fetch, no logging.
 * @rule        Entity-agnostic (UNIFIED-5): this module must never learn a car or
 *              noscut field name, a taxonomy string, or a business rule. Entity
 *              filtering lives in its own module per catalog — a "generic" module that
 *              secretly knows about cars breaks on the first reuse.
 * @universal   knowledge/universals/tools.md#listingQuery
 */

// === TYPES ==============================================================

/**
 * Shape of resolved Next.js search params.
 * @important Next.js 15 page props deliver `searchParams` as a PROMISE — the page
 *            component awaits it and passes the resolved object here. This module
 *            never awaits anything.
 */
export type SearchParamsInput = Record<string, string | string[] | undefined>;

/** One portion of a listing plus everything a pager and a counter need. */
export interface PageResult<T> {
  items: T[];
  page: number;
  totalPages: number;
  totalItems: number;
  hasMore: boolean;
  outOfRange: boolean;
  from: number;
  to: number;
}

// === CONSTANTS ==========================================================

/**
 * The single source of the portion size for every listing on the site.
 * @important The noscut catalog's local PAGE_SIZE is replaced by this constant in
 *            step D1 of series catalog-unified-listing — do not add a second size.
 */
export const LISTING_PAGE_SIZE = 24;

// === READERS ============================================================

/** Resolve a raw param to a single trimmed string, or null when absent/blank. */
function firstNonEmpty(raw: string | string[] | undefined): string | null {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * Read one search param as a trimmed string.
 * @section readers
 * @input  sp — resolved search params; key — param name.
 * @output The trimmed value, or null when absent / empty / whitespace-only.
 * @important A repeated param (`?brand=Audi&brand=BMW`) arrives as an array — the
 *            FIRST element wins; the listing has no multi-select semantics.
 */
export function readParam(sp: SearchParamsInput, key: string): string | null {
  return firstNonEmpty(sp[key]);
}

/**
 * Read the page number.
 * @section readers
 * @input  sp — resolved search params; key — param name (default "page").
 * @output An integer >= 1. Never throws, never returns 0 or a fraction.
 * @important Listing URLs arrive hand-edited, stale and crawler-generated, so garbage
 *            is the norm: only a pure digit string parsing to >= 1 is accepted —
 *            absent / "abc" / "0" / "-3" / "2.7" / "1e3" all fall back to 1.
 */
export function readPage(sp: SearchParamsInput, key = "page"): number {
  const raw = readParam(sp, key);
  if (raw === null || !/^\d+$/.test(raw)) return 1;
  const page = Number(raw);
  return page >= 1 ? page : 1;
}

// === PAGINATION =========================================================

/**
 * Slice one portion out of an already-filtered, already-sorted list.
 * @section pagination
 * @input  items — the full result set; page — requested page; pageSize — portion size.
 * @output PageResult: the portion plus page / totalPages / totalItems / hasMore /
 *         outOfRange / from / to (1-based inclusive counters for "показано N–M из K";
 *         both 0 for an empty list).
 * @important An out-of-range page is CLAMPED, not an error, and `outOfRange` reports
 *            it — the consumer can then noindex or 404 a bogus `?page=999` instead of
 *            silently serving duplicate content (crawl hygiene, UNIFIED-3). A page
 *            below 1 clamps to 1 defensively (readPage cannot produce one).
 */
export function paginate<T>(
  items: readonly T[],
  page: number,
  pageSize: number = LISTING_PAGE_SIZE,
): PageResult<T> {
  const size = Number.isFinite(pageSize) && pageSize >= 1 ? Math.floor(pageSize) : 1;
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / size));

  const requested = Number.isFinite(page) ? Math.floor(page) : 1;
  const clamped = Math.min(Math.max(requested, 1), totalPages);
  const outOfRange = requested !== clamped;

  const start = (clamped - 1) * size;
  const portion = items.slice(start, start + size);

  return {
    items: portion,
    page: clamped,
    totalPages,
    totalItems,
    hasMore: clamped < totalPages,
    outOfRange,
    from: totalItems === 0 ? 0 : start + 1,
    to: totalItems === 0 ? 0 : start + portion.length,
  };
}

// === URL BUILDING =======================================================

/**
 * Build a canonical listing URL from the current params plus a patch.
 * @section url
 * @input  basePath — the listing route (e.g. "/catalog"); sp — current params;
 *         patch — key → new value, or null to REMOVE the key.
 * @output basePath with a canonical query string, or bare basePath when no params
 *         remain (never a dangling "?").
 * @important Two rules that must not be "simplified away":
 *            (1) PAGE RESET — applying a patch removes "page" unless the patch itself
 *            names "page". Changing a filter while on page 5 would otherwise land the
 *            buyer on a page that no longer exists and show an empty grid.
 *            (2) CANONICAL ORDERING — query keys are sorted, so the same selection
 *            always yields a byte-identical URL (UNIFIED-7); differing key orders
 *            would otherwise create duplicate URLs for crawlers and split caching.
 *            Empty / whitespace values are dropped; encoding is URLSearchParams' job.
 */
export function buildListingHref(
  basePath: string,
  sp: SearchParamsInput,
  patch: Record<string, string | null>,
): string {
  const next = new Map<string, string>();
  for (const key of Object.keys(sp)) {
    const value = firstNonEmpty(sp[key]);
    if (value !== null) next.set(key, value);
  }

  for (const [key, value] of Object.entries(patch)) {
    const resolved = value === null ? null : firstNonEmpty(value);
    if (resolved === null) next.delete(key);
    else next.set(key, resolved);
  }

  // Page reset (rule 1): a filter change returns to the first page.
  if (!Object.prototype.hasOwnProperty.call(patch, "page")) next.delete("page");

  // Canonical ordering (rule 2): sorted keys → one URL per selection.
  const query = new URLSearchParams();
  for (const key of Array.from(next.keys()).sort()) {
    query.append(key, next.get(key) as string);
  }

  const qs = query.toString();
  return qs === "" ? basePath : `${basePath}?${qs}`;
}

/**
 * Report whether any filter is active.
 * @section url
 * @input  sp — current params; ignoreKeys — params that do not count as filters
 *         (default ["page"]).
 * @output true when at least one non-ignored param carries a non-empty value.
 * @important The consuming page uses this to decide `robots: noindex` for filtered
 *            states (UNIFIED-3). "page" is ignored BY DESIGN: a paginated page stays
 *            indexable per Google's pagination guidance — only real filter
 *            combinations drop out of the index.
 */
export function hasActiveFilters(
  sp: SearchParamsInput,
  ignoreKeys: readonly string[] = ["page"],
): boolean {
  return Object.keys(sp).some(
    (key) => !ignoreKeys.includes(key) && firstNonEmpty(sp[key]) !== null,
  );
}

/**
 * Keep a chosen value only while it is among the ones currently on offer.
 * @section url
 * @input  value — the parsed parameter, or null when absent; offered — the values the current
 *         data actually offers for that control.
 * @output `value` when `offered` contains it, otherwise null. An empty `offered` therefore
 *         always yields null, and a null value stays null.
 * @important THE STALE-PARAMETER RULE for DEPENDENT controls. When one control depends on
 *            another — model on make — the shared bar merges the new parent into the EXISTING
 *            params, so the address keeps the old child value. That leftover is not a request
 *            for an empty listing, it is a stale parameter, and obeying it hands the buyer an
 *            empty page with a control showing a value the parent does not offer.
 * @important Deliberately NOT the rule for ranges: `from > to` legitimately yields nothing,
 *            because the buyer typed both bounds on purpose. This rule is for a value the buyer
 *            never re-chose.
 * @important ENTITY-AGNOSTIC (UNIFIED-5): it learns no field name, no taxonomy string and no
 *            business rule — it only answers "is this value among the ones on offer". The caller
 *            supplies both sides, which is why one function serves both catalogs.
 */
export function narrowToOffered(value: string | null, offered: readonly string[]): string | null {
  if (value === null) return null;
  return offered.includes(value) ? value : null;
}
